(() => {
  if (window.__alpArticleTypographyLoaded) return;
  window.__alpArticleTypographyLoaded = true;

  const CONTENT_SELECTOR =
    '.shopify-section.alp-main-article .article-template__content.alp-article-prose';
  const CUSTOM_BLOCK_SELECTOR = '[class*="alp-article-custom-"]';
  const ARTICLE_SELECTOR = '.shopify-section.alp-main-article';
  const TOC_SELECTOR = '[data-article-toc]';

  const getColumnCount = (table) =>
    Array.from(table.rows).reduce((largestCount, row) => {
      const count = Array.from(row.cells).reduce(
        (rowCount, cell) => rowCount + Number(cell.colSpan || 1),
        0,
      );

      return Math.max(largestCount, count);
    }, 0);

  const enhanceTableInteractions = (table, wrapper, isInteractiveTable) => {
    wrapper._alpTableInteractionCleanup?.();
    wrapper.classList.toggle('has-column-interaction', isInteractiveTable);

    if (!isInteractiveTable) return;

    let activeColumn = -1;
    let hoveredColumn = -1;
    let focusedColumn = -1;
    let focusFrame = 0;
    const pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    const setActiveColumn = (columnIndex) => {
      if (activeColumn === columnIndex) return;
      activeColumn = columnIndex;

      Array.from(table.rows).forEach((row) => {
        Array.from(row.cells).forEach((cell) => {
          cell.classList.toggle('is-table-column-active', cell.cellIndex === columnIndex);
        });
      });
    };

    const renderActiveColumn = () => {
      setActiveColumn(hoveredColumn >= 0 ? hoveredColumn : focusedColumn);
    };

    const getCell = (target) => {
      const cell = target?.closest?.('th, td');
      return cell?.closest('table') === table ? cell : null;
    };

    const onPointerOver = (event) => {
      if (!pointerQuery.matches) return;
      const cell = getCell(event.target);
      if (!cell) return;
      hoveredColumn = cell.cellIndex;
      renderActiveColumn();
    };

    const onPointerLeave = () => {
      hoveredColumn = -1;
      renderActiveColumn();
    };

    const onFocusIn = (event) => {
      const cell = getCell(event.target);
      if (!cell) return;
      window.cancelAnimationFrame(focusFrame);
      focusedColumn = cell.cellIndex;
      renderActiveColumn();
    };

    const onFocusOut = () => {
      window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(() => {
        const focusedCell = getCell(document.activeElement);
        focusedColumn = focusedCell ? focusedCell.cellIndex : -1;
        renderActiveColumn();
      });
    };

    wrapper.addEventListener('pointerover', onPointerOver);
    wrapper.addEventListener('pointerleave', onPointerLeave);
    wrapper.addEventListener('focusin', onFocusIn);
    wrapper.addEventListener('focusout', onFocusOut);

    wrapper._alpTableInteractionCleanup = () => {
      window.cancelAnimationFrame(focusFrame);
      wrapper.removeEventListener('pointerover', onPointerOver);
      wrapper.removeEventListener('pointerleave', onPointerLeave);
      wrapper.removeEventListener('focusin', onFocusIn);
      wrapper.removeEventListener('focusout', onFocusOut);
      hoveredColumn = -1;
      focusedColumn = -1;
      setActiveColumn(-1);
      delete wrapper._alpTableInteractionCleanup;
    };
  };

  const enhanceTableSemantics = (table, wrapper) => {
    const columnCount = getColumnCount(table);
    const hasSpanningCells = table.querySelector('[colspan]:not([colspan="1"]), [rowspan]:not([rowspan="1"])');
    const labelWidth = Math.min(28, (100 / columnCount) * 1.12);
    const dataWidth = (100 - labelWidth) / Math.max(1, columnCount - 1);
    wrapper.style.setProperty('--article-table-columns', columnCount);
    wrapper.style.setProperty('--article-table-label-width', `${labelWidth}%`);
    wrapper.style.setProperty('--article-table-data-width', `${dataWidth}%`);
    const isComparisonTable = columnCount === 4 && !hasSpanningCells;
    wrapper.classList.toggle('is-comparison-table', isComparisonTable);
    enhanceTableInteractions(table, wrapper, isComparisonTable);

    Array.from(table.tHead?.rows[0]?.cells || []).forEach((cell) => {
      if (cell.tagName === 'TH' && !cell.hasAttribute('scope')) cell.scope = 'col';
    });

    Array.from(table.tBodies).forEach((body) => {
      Array.from(body.rows).forEach((row) => {
        const firstCell = row.cells[0];
        if (!firstCell) return;

        if (firstCell.tagName === 'TH') {
          if (!firstCell.hasAttribute('scope')) firstCell.scope = 'row';
        } else if (columnCount >= 4) {
          firstCell.setAttribute('role', 'rowheader');
        }
      });
    });
  };

  const wrapTables = (content) => {
    const tables = Array.from(content.querySelectorAll('table')).filter(
      (table) => !table.closest(CUSTOM_BLOCK_SELECTOR),
    );

    tables.forEach((table) => {
      const existingWrapper = table.parentElement?.classList.contains('alp-article-table-scroll')
        ? table.parentElement
        : null;

      if (existingWrapper) {
        enhanceTableSemantics(table, existingWrapper);
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'alp-article-table-scroll';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);

      enhanceTableSemantics(table, wrapper);
    });
  };

  const getArticleSections = (root) => {
    const sections = Array.from(root.querySelectorAll?.(ARTICLE_SELECTOR) || []);
    if (root.matches?.(ARTICLE_SELECTOR)) sections.unshift(root);
    return [...new Set(sections)];
  };

  const slugifyHeading = (text, index) => {
    const slug = text
      .normalize('NFKC')
      .toLowerCase()
      .trim()
      .replace(/[^\p{Letter}\p{Number}\p{Mark}]+/gu, '-')
      .replace(/^-+|-+$/g, '');

    return slug || `section-${index + 1}`;
  };

  const assignHeadingIds = (headings) => {
    const reservedIds = new Set(
      Array.from(document.querySelectorAll('[id]'))
        .filter((element) => !headings.includes(element))
        .map((element) => element.id),
    );

    headings.forEach((heading, index) => {
      const preferredId = heading.id.trim() || slugifyHeading(heading.textContent.trim(), index);
      let uniqueId = preferredId;
      let suffix = 2;

      while (reservedIds.has(uniqueId)) {
        uniqueId = `${preferredId}-${suffix}`;
        suffix += 1;
      }

      heading.id = uniqueId;
      reservedIds.add(uniqueId);
    });
  };

  const setHeadingFocus = (heading) => {
    const hadTabIndex = heading.hasAttribute('tabindex');
    if (!hadTabIndex) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });

    if (!hadTabIndex) {
      heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
    }
  };

  const initArticleToc = (section, content) => {
    const toc = section.querySelector(TOC_SELECTOR);
    if (!toc) return;

    section._alpArticleTocCleanup?.();

    const toggle = toc.querySelector('[data-article-toc-toggle]');
    const panel = toc.querySelector('[data-article-toc-panel]');
    const list = toc.querySelector('[data-article-toc-list]');
    const label = toc.querySelector('[data-article-toc-label]');
    const headings = Array.from(content.querySelectorAll('h2')).filter(
      (heading) => !heading.closest(CUSTOM_BLOCK_SELECTOR) && heading.textContent.trim(),
    );

    if (!toggle || !panel || !list || headings.length === 0) {
      toc.hidden = true;
      return;
    }

    assignHeadingIds(headings);
    list.replaceChildren();

    const linksByHeading = new Map();
    const fragment = document.createDocumentFragment();

    const setActiveHeading = (activeHeading) => {
      linksByHeading.forEach((tocLink, heading) => {
        const isActive = heading === activeHeading;
        tocLink.classList.toggle('active', isActive);
        if (isActive) {
          tocLink.setAttribute('aria-current', 'location');
        } else {
          tocLink.removeAttribute('aria-current');
        }
      });
    };

    headings.forEach((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      fragment.appendChild(item);
      linksByHeading.set(heading, link);

      link.addEventListener('click', () => {
        setActiveHeading(heading);
        window.requestAnimationFrame(() => setHeadingFocus(heading));
      });
    });

    list.appendChild(fragment);

    const setExpanded = (expanded) => {
      toc.classList.toggle('is-collapsed', !expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      panel.toggleAttribute('inert', !expanded);

      if (expanded) {
        panel.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }

      if (label) {
        label.textContent = expanded
          ? label.dataset.expandedLabel || 'Show less'
          : label.dataset.collapsedLabel || 'Show more';
      }
    };

    const isFirstInitialization = toc.dataset.articleTocInitialized !== 'true';
    const initialExpanded = isFirstInitialization
      ? !window.matchMedia('(max-width: 749px)').matches
      : toggle.getAttribute('aria-expanded') !== 'false';
    setExpanded(initialExpanded);
    toc.hidden = false;
    toc.dataset.articleTocInitialized = 'true';
    toggle.onclick = () => setExpanded(toggle.getAttribute('aria-expanded') !== 'true');

    let ticking = false;
    const updateActiveLink = () => {
      const activationLine = window.scrollY + Math.min(180, window.innerHeight * 0.25);
      let activeHeading = headings[0];

      headings.forEach((heading) => {
        const headingTop = heading.getBoundingClientRect().top + window.scrollY;
        if (headingTop <= activationLine) activeHeading = heading;
      });

      setActiveHeading(activeHeading);
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActiveLink);
    };

    updateActiveLink();
    window.addEventListener('scroll', onScroll, { passive: true });
    section._alpArticleTocCleanup = () => window.removeEventListener('scroll', onScroll);

    try {
      const initialId = decodeURIComponent(location.hash.slice(1));
      const initialHeading = headings.find((heading) => heading.id === initialId);
      if (initialHeading) {
        setActiveHeading(initialHeading);
        window.requestAnimationFrame(() => initialHeading.scrollIntoView({ behavior: 'auto', block: 'start' }));
      }
    } catch (_error) {
      // Ignore malformed URL fragments; the generated directory remains usable.
    }
  };

  const init = (root = document) => {
    getArticleSections(root).forEach((section) => {
      const content = section.querySelector(CONTENT_SELECTOR);
      if (!content) return;
      wrapTables(content);
      initArticleToc(section, content);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target._alpArticleTocCleanup?.();
    event.target.querySelectorAll?.('.alp-article-table-scroll').forEach((wrapper) => {
      wrapper._alpTableInteractionCleanup?.();
    });
  });
})();
